import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const SplashFlawless = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/onboarding-new');
    }, 3500);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ 
        background: 'linear-gradient(135deg, #16213E 0%, #0F3460 100%)'
      }}
    >
      {/* MASSIVE bright glow - SHINING effect */}
      <motion.div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(34, 211, 238, 0.3) 0%, rgba(139, 92, 246, 0.2) 30%, rgba(99, 102, 241, 0.1) 50%, transparent 70%)'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      />

      <div className="relative z-10 flex flex-col items-center">
        {/* Stars appear INSTANTLY - Like Dropbox logo */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <svg width="280" height="100" viewBox="0 0 280 100">
            <defs>
              {/* EXACT logo gradient - BRIGHTER */}
              <linearGradient id="shineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#22D3EE" stopOpacity="1" />
                <stop offset="25%" stopColor="#38BDF8" stopOpacity="1" />
                <stop offset="50%" stopColor="#818CF8" stopOpacity="1" />
                <stop offset="75%" stopColor="#A78BFA" stopOpacity="1" />
                <stop offset="100%" stopColor="#C084FC" stopOpacity="1" />
              </linearGradient>
              
              {/* STRONG glow for SHINING effect */}
              <filter id="strongGlow" x="-150%" y="-150%" width="400%" height="400%">
                <feGaussianBlur stdDeviation="12" result="blur"/>
                <feFlood floodColor="#22D3EE" floodOpacity="0.8"/>
                <feComposite in2="blur" operator="in" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Arc line - SHINING bright */}
            <motion.path
              d="M 50 85 Q 140 30, 230 85"
              stroke="url(#shineGrad)"
              strokeWidth="3"
              fill="none"
              opacity="0.9"
              filter="url(#strongGlow)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            />
            
            {/* 5 Stars - appear instantly, one after another */}
            {[
              { x: 50, y: 85, size: 14, delay: 0.1 },
              { x: 95, y: 56, size: 16, delay: 0.2 },
              { x: 140, y: 35, size: 20, delay: 0.3 },
              { x: 185, y: 56, size: 16, delay: 0.4 },
              { x: 230, y: 85, size: 14, delay: 0.5 },
            ].map((star, i) => (
              <motion.g
                key={i}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                }}
                transition={{ 
                  duration: 0.5, 
                  delay: star.delay,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
                {/* ULTRA BRIGHT white core - SHINING */}
                <circle 
                  cx={star.x} 
                  cy={star.y} 
                  r={star.size * 0.3} 
                  fill="#FFFFFF" 
                  opacity="1"
                  filter="url(#strongGlow)"
                />
                
                {/* 4-pointed star - EXACT logo style with SHINE */}
                <path
                  d={`
                    M ${star.x} ${star.y - star.size}
                    L ${star.x + star.size * 0.4} ${star.y - star.size * 0.4}
                    L ${star.x + star.size} ${star.y}
                    L ${star.x + star.size * 0.4} ${star.y + star.size * 0.4}
                    L ${star.x} ${star.y + star.size}
                    L ${star.x - star.size * 0.4} ${star.y + star.size * 0.4}
                    L ${star.x - star.size} ${star.y}
                    L ${star.x - star.size * 0.4} ${star.y - star.size * 0.4}
                    Z
                  `}
                  fill="url(#shineGrad)"
                  filter="url(#strongGlow)"
                />
                
                {/* Additional glow ring for SHINE effect */}
                <motion.circle
                  cx={star.x}
                  cy={star.y}
                  r={star.size * 1.5}
                  fill="none"
                  stroke="url(#shineGrad)"
                  strokeWidth="2"
                  opacity="0.4"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.4, 0.6, 0.4],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </motion.g>
            ))}
          </svg>
        </motion.div>

        {/* "taraanaya" text - SHINING BRIGHT */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* MASSIVE cyan glow - SHINING */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontSize: '100px',
              fontWeight: 400,
              color: '#22D3EE',
              letterSpacing: '5px',
              fontStyle: 'italic',
              filter: 'blur(50px)',
              opacity: 0.9,
            }}
          >
            taraanaya
          </div>
          
          {/* White glow layer for extra SHINE */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontSize: '100px',
              fontWeight: 400,
              color: '#FFFFFF',
              letterSpacing: '5px',
              fontStyle: 'italic',
              filter: 'blur(20px)',
              opacity: 0.6,
            }}
          >
            taraanaya
          </div>
          
          {/* Main gradient text - ULTRA BRIGHT */}
          <h1
            style={{
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontSize: '100px',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #22D3EE 0%, #38BDF8 20%, #60A5FA 40%, #818CF8 60%, #A78BFA 80%, #C084FC 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '5px',
              textAlign: 'center',
              fontStyle: 'italic',
              filter: 'drop-shadow(0 0 60px rgba(34, 211, 238, 0.9)) drop-shadow(0 0 100px rgba(139, 92, 246, 0.7)) drop-shadow(0 0 140px rgba(99, 102, 241, 0.5))',
              position: 'relative',
            }}
          >
            taraanaya
          </h1>

          {/* BRIGHT underline with glow */}
          <motion.div
            className="mx-auto mt-6"
            style={{
              width: 200,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #22D3EE 20%, #818CF8 50%, #C084FC 80%, transparent)',
              boxShadow: '0 0 20px rgba(34, 211, 238, 0.6), 0 0 40px rgba(139, 92, 246, 0.4)',
              opacity: 0.8,
            }}
            initial={{ width: 0 }}
            animate={{ width: 200 }}
            transition={{ duration: 0.8, delay: 1.3 }}
          />
        </motion.div>

        {/* STRONG pulsing SHINE effect */}
        <motion.div
          className="absolute"
          style={{
            width: '800px',
            height: '800px',
            background: 'radial-gradient(circle, rgba(34, 211, 238, 0.25) 0%, rgba(139, 92, 246, 0.15) 40%, transparent 70%)',
            filter: 'blur(100px)',
            pointerEvents: 'none',
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.6, 0.9, 0.6],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
    </div>
  );
};

export default SplashFlawless;
